import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import API_URL from '../api/baseURL';
import { trackPixelCustom } from '../utils/metaPixel';

export default function ActivateAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [ghostInfo, setGhostInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Fetch ghost user info on mount
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/auth/activate/${token}`);
        setGhostInfo(data.user);
      } catch (err) {
        // The backend now distinguishes expired / already-activated / unknown,
        // and each needs a different action from the client. Before this they
        // all rendered as "Lien expiré", which told someone whose account was
        // already active to go and ask the workshop for another link.
        setErrorCode(err.response?.data?.errorCode || '');
        setError(err.response?.data?.message || 'Lien invalide ou expiré');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (password.length < 8) {
      setSubmitError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Les mots de passe ne correspondent pas');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/activate/${token}`, { password });
      // Meta Pixel: a walk-in/ghost client claimed & activated their account
      trackPixelCustom('AccountActivated');
      // Auto-login
      login(data.token);
      navigate('/dashboard');
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Erreur lors de l\'activation');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0E27' }}>
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  // Invalid/expired token
  if (error) {
    // An already-activated account is not a failure — the person has a working
    // login and just re-opened an old SMS. Showing it in red under "Lien
    // expiré" sent them back to the workshop for a link they do not need.
    const activated = errorCode === 'ACCOUNT_ALREADY_ACTIVATED';
    const title = activated ? 'Compte déjà activé'
      : errorCode === 'ACTIVATION_TOKEN_EXPIRED' ? 'Lien expiré'
        : 'Lien invalide';

    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0E27' }}>
        <div className={`max-w-md w-full p-8 rounded-2xl border bg-slate-900/80 text-center ${
          activated ? 'border-cyan-500/30' : 'border-red-500/30'}`}>
          {activated
            ? <ShieldCheck className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            : <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />}
          <h1 className="text-xl font-bold text-white mb-2">{title}</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <a
            href="/login"
            className="inline-block px-6 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold transition-colors"
          >
            Aller à la connexion
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0E27' }}>
      <div className="max-w-md w-full p-8 rounded-2xl border border-cyan-500/20 bg-slate-900/80 shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <ShieldCheck className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Activez votre compte</h1>
          <p className="text-slate-400 text-sm">
            Bienvenue <span className="text-cyan-300 font-medium">{ghostInfo?.name}</span>, choisissez un mot de passe pour accéder à votre espace 1PARC.
          </p>
        </div>

        {/* Info display */}
        <div className="mb-6 p-4 rounded-xl bg-slate-800/60 border border-slate-700/50 space-y-2">
          {ghostInfo?.name && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Nom</span>
              <span className="text-white font-medium">{ghostInfo.name}</span>
            </div>
          )}
          {ghostInfo?.email && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Email</span>
              <span className="text-white font-medium">{ghostInfo.email}</span>
            </div>
          )}
          {ghostInfo?.phoneNumber && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Téléphone</span>
              <span className="text-white font-medium">{ghostInfo.phoneNumber}</span>
            </div>
          )}
        </div>

        {/* Password form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Mot de passe</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 caractères"
                autoComplete="new-password"
                className="w-full pl-4 pr-12 py-3 rounded-xl bg-slate-800/80 border border-slate-700/50 text-white placeholder-slate-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 outline-none transition-all"
                required
                minLength={8}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Confirmer le mot de passe</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Retapez le mot de passe"
              autoComplete="new-password"
              className="w-full px-4 py-3 rounded-xl bg-slate-800/80 border border-slate-700/50 text-white placeholder-slate-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 outline-none transition-all"
              required
              minLength={8}
            />
          </div>

          {submitError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-base transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Activation...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                Activer mon compte
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
